import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getCoupleMeCoupleGet,
  couplePreviewMeCouplePreviewGet,
  selectPartnerMeSelectPartnerPost,
  patchAnswersMePartnersPartnerIdAnswersPatch,
  postContactMePartnersPartnerIdContactPost,
  smsHandoffMePartnersPartnerIdSmsHandoffPost,
  flagMissingEmailMePartnersPartnerIdFlagMissingEmailPost,
  reportWrongNamesMeCoupleReportWrongNamesPost,
} from "../__generated__/api/me/me";
import type {
  AnswersPatch,
  CoupleOut,
  CouplePreviewOut,
  PartnerOut,
  SelectPartnerOut,
  SmsHandoffOut,
} from "../__generated__/api/wrenAPI.schemas";
import { EMPTY_ONBOARDING_STATE } from "./types";
import {
  EMPTY_ANSWERS,
  type ChildrenStatus,
  type ConsentRecord,
  type ContactDraft,
  type Couple,
  type FocusArea,
  type OnboardingAnswers,
  type OnboardingState,
  type Partner,
  type RelationshipLength,
  type RelationshipStatus,
  type SmsHandoffResponse,
} from "./types";
import { useMeAuth } from "./auth/MeAuthContext";
import type { CustomRequestInit } from "../api/client";

// ─────────────────────────────────────────────────────────────────────────────
// /me onboarding hooks — production wiring.
//
// These wrap the orval-generated /me hooks (`__generated__/api/me/me.ts`) and
// adapt the snake_case wire contract (`CoupleOut`, `AnswersPatch`, `ConsentIn`,
// `SmsHandoffOut`) ⇄ the camelCase view types in `types.ts` that the step
// components consume. The component contract (`useMeCouple`, `useSaveAnswers`,
// …) is identical to the prototype, so the steps port verbatim.
//
// The acting partner is derived from the JWT server-side (`get_current_person`):
// the couple read returns both partners; the partner the token belongs to is
// resolved on the client from the JWT `person_id` (see `MeApp`). Writes target a
// partnerId and the backend enforces "may only write own partnerId".
// ─────────────────────────────────────────────────────────────────────────────

export interface QueryShape<T> {
  data: T | undefined;
  isLoading: boolean;
  error: Error | null;
}

// ── snake_case wire → camelCase view adapters ───────────────────────────────

function answersFromWire(raw: PartnerOut["answers"]): OnboardingAnswers {
  const a = (raw ?? {}) as Record<string, unknown>;
  const focus = Array.isArray(a.focus_areas)
    ? (a.focus_areas as FocusArea[])
    : [...EMPTY_ANSWERS.focusAreas];
  return {
    confirmedName: (a.confirmed_name as string | null) ?? null,
    preferredName: (a.preferred_name as string | null) ?? null,
    timezone: (a.timezone as string | null) ?? null,
    relationshipStatus: (a.relationship_status as RelationshipStatus | null) ?? null,
    relationshipStatusOther: (a.relationship_status_other as string | null) ?? null,
    relationshipLength: (a.relationship_length as RelationshipLength | null) ?? null,
    children: (a.children as ChildrenStatus | null) ?? null,
    focusAreas: focus,
    focusAreasOther: (a.focus_areas_other as string | null) ?? null,
    sixMonthsFromNow: (a.six_months_from_now as string | null) ?? null,
  };
}

function consentFromWire(c: PartnerOut["consent"]): ConsentRecord | null {
  if (!c) return null;
  // `PartnerOut.consent` is a loose object; only surface a record when the
  // signed signature is present (mirrors the prototype's null-until-signed).
  const obj = c as Record<string, unknown>;
  if (!obj.typed_signature) return null;
  return {
    therapyConsent: !!obj.therapy_consent,
    platformConsent: !!obj.platform_consent,
    transcriptionConsent: !!obj.transcription_consent,
    typedSignature: String(obj.typed_signature),
    signedAt: String(obj.signed_at ?? ""),
  };
}

function partnerFromWire(p: PartnerOut): Partner {
  const onboardingState: OnboardingState = {
    step: "welcome",
    furthestStep: "welcome",
    answers: answersFromWire(p.answers),
    consent: consentFromWire(p.consent),
    completedAt: p.progress?.completed ? new Date().toISOString() : null,
  };
  return {
    id: p.id,
    firstName: p.first_name,
    lastName: p.last_name,
    email: p.email ?? null,
    phone: p.phone ?? null,
    avatarUrl: p.avatar_url ?? null,
    onboardingState,
  };
}

export function coupleFromWire(c: CoupleOut): Couple {
  return {
    id: c.id,
    actingPartnerId: c.acting_partner_id,
    therapistId: c.therapist?.id != null ? String(c.therapist.id) : "",
    therapistName: c.therapist?.name ?? "your therapist",
    therapistContactEmail: c.therapist?.contact_email ?? "",
    firstSessionAt: c.first_session_at ?? null,
    firstSessionDurationMinutes: c.first_session_duration_minutes ?? null,
    firstSessionLocation: c.first_session_location ?? null,
    firstSessionLocationAddress: c.first_session_location_address ?? null,
    partners: (c.partners ?? []).map(partnerFromWire),
  };
}

/**
 * Adapt the least-privilege picker roster (`CouplePreviewOut`) into a partial
 * `Couple` the pre-bind S1/S2 screens consume. The preview deliberately omits
 * person-bound data (therapist, session, onboarding answers/consent), so those
 * fields carry safe placeholders — only `id` + `partners[].{id,firstName,
 * lastName}` are real, which is all the picker reads. The full couple read
 * replaces this the moment a partner is selected (post-bind person token).
 *
 * `actingPartnerId` is intentionally empty: nobody is bound yet, so `MeShell`
 * resolves `partner` to null and the picker stays a self-select (both enabled).
 */
export function coupleFromPreview(p: CouplePreviewOut): Couple {
  return {
    id: p.id,
    actingPartnerId: "",
    therapistId: "",
    therapistName: "your therapist",
    therapistContactEmail: "",
    firstSessionAt: null,
    // Pre-bind, like the session itself: the preview withholds person-bound data.
    firstSessionDurationMinutes: null,
    firstSessionLocation: null,
    firstSessionLocationAddress: null,
    partners: (p.partners ?? []).map((m) => ({
      id: m.id,
      firstName: m.first_name,
      lastName: m.last_name,
      // Pre-bind roster carries no contact info; the full couple read (post-bind)
      // supplies the real email/phone the "A bit about you" step pre-fills.
      email: null,
      phone: null,
      avatarUrl: null,
      onboardingState:
        typeof structuredClone === "function"
          ? structuredClone(EMPTY_ONBOARDING_STATE)
          : (JSON.parse(JSON.stringify(EMPTY_ONBOARDING_STATE)) as typeof EMPTY_ONBOARDING_STATE),
    })),
  };
}

// ── camelCase view → snake_case wire adapters ───────────────────────────────

function answersToWire(patch: Partial<OnboardingAnswers>): AnswersPatch {
  const out: AnswersPatch = {};
  if ("confirmedName" in patch) out.confirmed_name = patch.confirmedName ?? null;
  if ("preferredName" in patch) out.preferred_name = patch.preferredName ?? null;
  if ("timezone" in patch) out.timezone = patch.timezone ?? null;
  if ("relationshipStatus" in patch)
    out.relationship_status = patch.relationshipStatus ?? null;
  if ("relationshipStatusOther" in patch)
    out.relationship_status_other = patch.relationshipStatusOther ?? null;
  if ("relationshipLength" in patch)
    out.relationship_length = patch.relationshipLength ?? null;
  if ("children" in patch) out.children = patch.children ?? null;
  if ("focusAreas" in patch) out.focus_areas = patch.focusAreas ?? null;
  if ("focusAreasOther" in patch)
    out.focus_areas_other = patch.focusAreasOther ?? null;
  if ("sixMonthsFromNow" in patch)
    out.six_months_from_now = patch.sixMonthsFromNow ?? null;
  return out;
}

// ── hooks ───────────────────────────────────────────────────────────────────

const COUPLE_KEY = ["me", "couple"] as const;
const COUPLE_PREVIEW_KEY = ["me", "couple", "preview"] as const;
const ME_COUPLE_WAITING_REFETCH_MS = 5000;

/**
 * Read the acting partner's couple (both partners' embedded onboarding state).
 *
 * Requires the PERSON-bound (post-bind) token. `enabled` lets `MeShell` hold
 * this query off while only the couple-scoped (pre-bind) token is held — calling
 * `/me/couple` with a couple token 401s, which would wipe the session.
 */
export function useMeCouple(
  options: { enabled?: boolean } = {}
): QueryShape<Couple> {
  const { enabled = true } = options;
  const { token } = useMeAuth();
  const q = useQuery({
    queryKey: [...COUPLE_KEY, token ?? "anonymous"],
    enabled,
    queryFn: async () => {
      const res = await getCoupleMeCoupleGet();
      return coupleFromWire(res.data as CoupleOut);
    },
    refetchInterval: (query) => {
      const couple = query.state.data;
      if (!couple) return false;
      // Poll while ANY partner is still onboarding. This covers two distinct
      // waits off the same read:
      //   • the ACTING partner on the S9 handoff, waiting for their own inbound
      //     SMS to land (their 1:1 created → progress.completed flips), and
      //   • either partner on S11, waiting for the OTHER to finish.
      // The old gate keyed only on the non-acting partner; for the SECOND
      // partner to onboard the other is already complete, so it returned `false`
      // and never polled — stranding the acting user on "Waiting for your text…"
      // because their own join was never observed.
      return couple.partners.some((p) => !p.onboardingState.completedAt)
        ? ME_COUPLE_WAITING_REFETCH_MS
        : false;
    },
  });
  return {
    data: q.data,
    isLoading: q.isLoading,
    error: q.error instanceof Error ? q.error : null,
  };
}

/**
 * Read the least-privilege picker roster off the COUPLE-scoped (pre-bind) token.
 *
 * `/me/couple/preview` returns only both partners' display identity (no
 * onboarding data), which is all the self-select picker needs. `enabled` keeps
 * it off until the couple token is actually held (post-bind, the full
 * `useMeCouple` read takes over).
 */
export function useCouplePreview(
  options: { enabled?: boolean } = {}
): QueryShape<Couple> {
  const { enabled = true } = options;
  const { token } = useMeAuth();
  const q = useQuery({
    queryKey: [...COUPLE_PREVIEW_KEY, token ?? "anonymous"],
    enabled,
    queryFn: async () => {
      const res = await couplePreviewMeCouplePreviewGet();
      return coupleFromPreview(res.data as CouplePreviewOut);
    },
  });
  return {
    data: q.data,
    isLoading: q.isLoading,
    error: q.error instanceof Error ? q.error : null,
  };
}

/**
 * Report that neither name on the picker roster belongs to the opener.
 *
 * Fire-and-forget by design. The endpoint's whole job is to raise a Slack alert for an
 * operator, and the screen that calls it is showing someone who has just told us our
 * data about them is wrong. Whether our alerting reached Slack is not their problem, so
 * a rejection here is logged and swallowed rather than rendered.
 *
 * `skip401Redirect` for the same reason: the endpoint is couple-scoped (S2 is pre-bind),
 * and the global 401 handler CLEARS the session and bounces to `/me/auth?expired`. A
 * diagnostic that can log the client out is worse than a diagnostic that goes unheard.
 */
export function useReportWrongNames() {
  return useMutation<void, Error, void>({
    mutationFn: async () => {
      await reportWrongNamesMeCoupleReportWrongNamesPost({
        skip401Redirect: true,
      } as CustomRequestInit);
    },
  });
}

/**
 * Self-select a partner: bind the couple-scoped session to one partner and mint
 * the person-bound token. The caller stores the returned token (overwriting the
 * couple token), which flips `MeShell` from the pre-bind picker to the full
 * person-scoped onboarding. `secret` is reserved for a future PIN gate (the
 * backend validator always passes today).
 */
export function useSelectPartner() {
  return useMutation<string, Error, string>({
    mutationFn: async (partnerId: string) => {
      const res = await selectPartnerMeSelectPartnerPost({ partner_id: partnerId });
      return (res.data as SelectPartnerOut).token;
    },
  });
}

// After a /me mutation the couple read can change, so refresh it.
function useInvalidateCouple() {
  const { token } = useMeAuth();
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: [...COUPLE_KEY, token ?? "anonymous"] });
}

/**
 * Persist one partner's in-flight onboarding answers.
 *
 * The mutation input is a PARTIAL patch — the backend merges it onto the stored
 * form_data (server-authoritative), so the client never read-modify-writes a
 * possibly-stale snapshot. The invalidate → refetch brings the merged state back.
 */
export function useSaveAnswers(partnerId: string) {
  const invalidate = useInvalidateCouple();
  return useMutation({
    mutationFn: (patch: Partial<OnboardingAnswers>) =>
      patchAnswersMePartnersPartnerIdAnswersPatch(partnerId, answersToWire(patch)),
    onSuccess: invalidate,
  });
}

/**
 * Persist the acting partner's contact (email + phone) entered on the "A bit about
 * you" step (S3Identity).
 *
 * Both fields are optional; an empty draft is a valid no-op server-side. On success we
 * invalidate the couple so the refetched `email`/`phone` reflect the edit (and re-prefill
 * correctly on a back-nav). The wire contract (`ContactIn`) is single-word fields, so the
 * camelCase draft maps 1:1.
 */
export function useSaveContact(partnerId: string) {
  const invalidate = useInvalidateCouple();
  return useMutation({
    mutationFn: (draft: ContactDraft) =>
      postContactMePartnersPartnerIdContactPost(partnerId, {
        email: draft.email,
        phone: draft.phone,
      }),
    onSuccess: invalidate,
  });
}

/** Request the SMS deep link + access code for this partner (S9 handoff). */
export function useRequestSmsHandoff(partnerId: string) {
  return useMutation<SmsHandoffResponse, Error, void>({
    mutationFn: async () => {
      const res = await smsHandoffMePartnersPartnerIdSmsHandoffPost(partnerId);
      // customInstance throws on non-2xx, so a resolved `data` is the success
      // payload (the orval union includes the 422 error shape).
      const out = res.data as SmsHandoffOut;
      return {
        accessCode: out.access_code,
        smsUri: out.sms_uri,
        dovaPhoneNumber: out.dova_phone_number,
        messageBody: out.message_body,
      };
    },
  });
}

/** Flag that this partner is missing an email (therapist-facing). */
export function useFlagMissingEmail(partnerId: string) {
  return useMutation({
    mutationFn: () =>
      flagMissingEmailMePartnersPartnerIdFlagMissingEmailPost(partnerId),
  });
}
